import React, { useState, useMemo, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Link, useLocation } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import NRSwapIcon from '../../NRIcons/NRSwapIcon';
import { useQuery } from '@apollo/client';
import useNRMutation from '../../graphql/UseNRMutation';
import NRButtonProgress from '../../components/NRButtonProgress/NRButtonProgress';
import NRAutocomplete from '../../components/NRAutocomplete/NRAutocomplete';
import { GET_ASSETS_NAMES } from '../../graphql/queries/asset';
import { CREATE_ASSET_COMPARISON_REPORT } from '../../graphql/queries/reports';
import { CONSTANTS } from '../../constants/constants';
import { useTheme } from '@mui/material/styles';

function NRCreateReportModal({ open, onClose, onReportCreated, initialAssets = [] }) {
  const theme = useTheme();
  const location = useLocation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [assetA, setAssetA] = useState([]);
  const [assetB, setAssetB] = useState([]);

  useEffect(() => {
    if (open && initialAssets?.length === 2) {
      setAssetA([initialAssets[0]]);
      setAssetB([initialAssets[1]]);
    }
  }, [open, initialAssets]);

  const { data, loading } = useQuery(GET_ASSETS_NAMES, {
    context: { version: 'v3' },
    fetchPolicy: 'cache-first',
    variables: {
      args: {
        filter: {},
        cursor: { first: 10000 },
        sort: {
          field: CONSTANTS.TABLE.ASSETS.FILTERS.FIELDS.NAME,
          order: CONSTANTS.TABLE.ASSETS.FILTERS.ORDER.ASC
        }
      }
    }
  });

  const assets = useMemo(() => data?.assetsRelay?.edges?.map(e => ({ ...e.node, id: e.node.assetId })) || [], [data]);

  const [createReport, , mutationLoading] = useNRMutation(CREATE_ASSET_COMPARISON_REPORT, 'createAssetComparisonReport', null, null, {
    onCompleted: () => {
      onReportCreated?.();
      handleClose();
      let snackbarKey;
      const isOnReportsPage = location.pathname === '/reports';

      const message = isOnReportsPage ? (
        <Typography variant="body2">{CONSTANTS.REPORT.CREATE_REPORT_SUCCESS.split(',')[0]}</Typography>
      ) : (
        <Typography variant="body2">
          {CONSTANTS.REPORT.CREATE_REPORT_SUCCESS}
          <Link
            to="/reports"
            onClick={() => {
              if (snackbarKey) closeSnackbar(snackbarKey);
            }}
            style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 'bold' }}
          >
            {CONSTANTS.REPORT.REPORTS}
          </Link>
          {CONSTANTS.REPORT.CREATE_REPORT_SUCCESS_2}
        </Typography>
      );

      snackbarKey = enqueueSnackbar(message, {
        variant: 'success',
        onClick: () => {
          if (snackbarKey) closeSnackbar(snackbarKey);
        }
      });
    }
  });

  const handleClose = () => {
    setAssetA([]);
    setAssetB([]);
    onClose();
  };

  const handleSwap = () => {
    if (assetA.length > 0 || assetB.length > 0) {
      const tempA = assetA;
      setAssetA(assetB);
      setAssetB(tempA);
    }
  };

  const handleCreate = () => {
    if (assetA.length > 0 && assetB.length > 0) {
      createReport({
        variables: {
          args: {
            assetA: assetA[0].id,
            assetB: assetB[0].id
          }
        }
      });
    }
  };

  const filteredAssetsForB = useMemo(() => {
    if (assetA.length === 0) return [];
    return assets?.filter(asset => asset.id !== assetA[0].id) || [];
  }, [assets, assetA]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="nr-create-report-dialog-title"
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          backgroundImage: 'none'
        }
      }}
    >
      <DialogTitle id="nr-create-report-dialog-title">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant={'h3'} color={'text.primary'}>
            Create Comparison Report
          </Typography>
          <IconButton aria-label="close" onClick={handleClose} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography tabIndex={0} variant="body2" color="text.secondary">
            Select two assets to compare. The{' '}
            <Typography variant="subtitle2" component="span" color="text.secondary">
              Compare To (Target)
            </Typography>{' '}
            asset will receive all difference metrics relative to the baseline.
          </Typography>
          <NRAutocomplete
            multiple={false}
            fullWidth
            arrayData={assets || []}
            value={assetA}
            setValue={v => {
              if (v?.[0]?.id !== assetA?.[0]?.id) {
                setAssetA(v);
                setAssetB([]);
              }
            }}
            loading={loading}
            fieldToShow={'name'}
            idField={'id'}
            dataNameSingular={'Asset'}
            color="secondary"
            label="Compare From (Baseline)"
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Differences applied to
            </Typography>
            <ArrowDownwardIcon sx={{ width: 20, height: 20, color: theme.palette.text.iconInfo }} />
          </Box>
          <NRAutocomplete
            multiple={false}
            fullWidth
            arrayData={filteredAssetsForB}
            value={assetB}
            setValue={v => {
              if (v?.[0]?.id !== assetB?.[0]?.id) {
                setAssetB(v);
              }
            }}
            loading={loading}
            fieldToShow={'name'}
            idField={'id'}
            dataNameSingular={'Asset'}
            color="secondary"
            label="Compare To (Target)"
            disabled={assetA.length === 0}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Button
            onClick={handleSwap}
            size="small"
            disabled={assetA.length === 0 || assetB.length === 0}
            startIcon={<NRSwapIcon width={20} height={20} />}
            sx={{
              mr: 'auto',
              textTransform: 'none',
              fontWeight: 'bold',
              padding: theme.spacing(0.75, 2, 0.75, 1.5),
              gap: theme.spacing(1),
              color: theme.palette.text.iconInfo,
              '&:hover': {
                backgroundColor: 'action.hover'
              },
              '&.Mui-disabled': {
                color: 'action.disabled'
              }
            }}
          >
            Swap
          </Button>
          <Box display="flex" gap={2}>
            <Button variant="outlined" color="secondary" onClick={handleClose} sx={{ textTransform: 'none', fontWeight: 'bold', px: 3 }}>
              Cancel
            </Button>
            <NRButtonProgress
              label="Create"
              color="secondary"
              onClick={handleCreate}
              isLoading={mutationLoading}
              disabled={assetA.length === 0 || assetB.length === 0}
              fullWidth={false}
              extraStyles={{ textTransform: 'none', fontWeight: 'bold', px: 3 }}
            />
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default NRCreateReportModal;
